/**
 * When adding new brands or modifying colors, update this file only.
 * All components will automatically inherit the changes.
 */

export const BRAND_COLORS: Record<string, string> = {
  Ajinomoto: "#F24A50", // red
  "Jose Ole": "#E066A6", // purple
  "Tai Pei": "#D8B85A", // Gold
  "Ling Ling": "#FFE35C", // yellow
};

/**
 * Tailwind-scale fallback colors chosen for chart contrast when a brand,
 * media type, ad group, or TV channel does not have a named mapping.
 */
export const DEFAULT_COLORS = [
  "#3b82f6", // blue-500
  "#1e40af", // blue-900
  "#06b6d4", // cyan-500
  "#10b981", // emerald-500
  "#f59e0b", // amber-500
  "#ef4444", // red-500
  "#8b5cf6", // violet-500
  "#f97316", // orange-500
  "#ec4899", // pink-500
  "#6b7280", // gray-500
];

export const AD_GROUP_COLORS: Record<string, string> = {
  Keywords: "#10b981", // emerald-500 - Top performer
  "Contextual - Packaged": "#3b82f6", // blue-500
  "Contextual - Frozen": "#06b6d4", // cyan-500
  "Contextual - Frozen Food": "#8b5cf6", // purple-500
  "Behavioral - Category - Recent - Packaged": "#f59e0b", // amber-500
  "Behavioral - Personas - Lifestyles - Foodies": "#ef4444", // red-500
  "Behavioral - Personas - Lifestyle - Travelers": "#ec4899", // pink-500
  "Behavioral - Personas - Lifestyle - Luxury Travelers": "#a855f7", // purple-400
  "Behavioral - Category - Recent - Tofu & Soy": "#14b8a6", // teal-500
  "Behavioral - Personas - Lifestyle - Hosting & Entertaining": "#f97316", // orange-500
  "Contextual - Tofu & Soy": "#84cc16", // lime-500
};

export const MEDIA_COLORS: Record<string, string> = {
  Video: "#ef4444", // red-500
  Display: "#06b6d4", // cyan-500
};

export const CHANNEL_COLORS: Record<string, string> = {
  "Franchises TV OTT": "#3b82f6", // blue-500
  "Golf Channel": "#10b981", // emerald-500
  "PGA Tour": "#f59e0b", // amber-500
};

export const DEFAULT_FALLBACK_COLOR = "#6b7280"; // gray-500

export function generateBrandColors(
  brandNames: string[],
  enableDebugLogging = false,
): Record<string, string> {
  const colorMap: Record<string, string> = {};

  brandNames.forEach((brandName, index) => {
    if (BRAND_COLORS[brandName]) {
      colorMap[brandName] = BRAND_COLORS[brandName];
    } else {
      colorMap[brandName] = DEFAULT_COLORS[index % DEFAULT_COLORS.length];
    }

    if (enableDebugLogging && process.env.NODE_ENV === "development") {
      console.log(
        `[COLOR] Brand color assigned: ${brandName} -> ${colorMap[brandName]}`,
      );
    }
  });

  return colorMap;
}

export function getBrandColor(
  brandName: string,
  fallbackIndex: number = 0,
): string {
  return (
    BRAND_COLORS[brandName] ||
    DEFAULT_COLORS[fallbackIndex % DEFAULT_COLORS.length]
  );
}

export function getAdGroupColor(adGroupName: string): string {
  return AD_GROUP_COLORS[adGroupName] || DEFAULT_FALLBACK_COLOR;
}

export function getMediaColor(mediaType: string): string {
  return MEDIA_COLORS[mediaType] || DEFAULT_FALLBACK_COLOR;
}

export function getChannelColor(channelName: string): string {
  return CHANNEL_COLORS[channelName] || DEFAULT_FALLBACK_COLOR;
}
